import { AxiosError } from "axios";

import { ADMIN_API_ENDPOINTS } from "@/config/apiEndpoints/adminUrls";
import { handleApiError } from "@/lib/utils";
import { UNIVERSITY_ID } from "@/constants/admin";
import { upsertUniversitySettings, getUniversitySettingsBySection, getAllUniversitySettings } from "./universitySettings";
import { UNIVERSITY_SETTINGS_SECTION_TYPES } from "@/constants/admin";
import adminAxios from "@/lib/axios";

export interface ICourse {
  id?: number;
  university_settings_id?: number;
  program_name?: string;
  // program_value?: string;
  erp_unicode?: string;
  pok?: string;
  priority?: number;
  brochure?: string;
  variant?: string;
  is_active?: boolean;
  /**
   * Certification programme (CMS toggle on the course row). Drives which lead-form
   * flow the selected Area of Interest runs. MySQL returns TINYINT(1), so this can
   * arrive as `0`/`1` rather than a real boolean — read it through
   * `isCmsShowFlagOn(value, false)` instead of a bare truthiness check.
   */
  is_certification?: boolean | number | null;
  lms_program_batch_id?: number | string | null;
}

/**
 * Set course variant for university (upsert)
 */
export async function setCourseVariant(variant: string, isActive: boolean = true) {
  try {
    const response = await upsertUniversitySettings(
      UNIVERSITY_SETTINGS_SECTION_TYPES.COURSES,
      variant,
      isActive
    );
    return response;
  } catch (error) {
    const errorResponse = handleApiError(error as AxiosError);
    return errorResponse ? errorResponse : null;
  }
}

/**
 * Create a new course
 */
export async function createCourse(payload: Partial<ICourse>) {
  try {
    const response = await adminAxios.post(ADMIN_API_ENDPOINTS.COURSE.CREATE, payload);
    return response?.status === 201 ? response?.data : null;
  } catch (error) {
    const errorResponse = handleApiError(error as AxiosError);
    return errorResponse ? errorResponse : null;
  }
}

/**
 * Get courses by variant
 * Returns empty array silently for 404 (expected when variant doesn't exist yet)
 */
export async function getCoursesByVariant(variant: string) {
  try {
    const response = await adminAxios.get(
      ADMIN_API_ENDPOINTS.COURSE.GET_BY_UNIVERSITY_AND_VARIANT(UNIVERSITY_ID, variant)
    );
    return response?.status === 200 ? (response?.data?.data || response?.data || []) : [];
  } catch (error) {
    const axiosError = error as AxiosError;
    // 404 is expected when checking if data exists - return empty silently
    if (axiosError.response?.status === 404) {
      return [];
    }
    handleApiError(axiosError);
    return [];
  }
}

/**
 * Get courses by university_settings_id
 */
export async function getCoursesByUniversitySettingsId(universitySettingsId: number) {
  try {
    const response = await adminAxios.get(
      ADMIN_API_ENDPOINTS.COURSE.GET_BY_UNIVERSITY_SETTINGS_ID(universitySettingsId)
    );
    return response?.status === 200 ? (response?.data?.data || response?.data || []) : [];
  } catch (error) {
    const errorResponse = handleApiError(error as AxiosError);
    return errorResponse ? [] : [];
  }
}

/**
 * Get all courses by university
 */
export async function getAllCourses() {
  try {
    const response = await adminAxios.get(ADMIN_API_ENDPOINTS.COURSE.GET_BY_UNIVERSITY_ID(UNIVERSITY_ID));
    return response?.status === 200 ? response?.data : null;
  } catch (error) {
    const errorResponse = handleApiError(error as AxiosError);
    return errorResponse ? errorResponse : null;
  }
}

/**
 * Get only active courses by university (linked to active settings)
 */
export async function getActiveCourses() {
  try {
    const response = await adminAxios.get(
      ADMIN_API_ENDPOINTS.COURSE.GET_ACTIVE_BY_UNIVERSITY_ID(UNIVERSITY_ID)
    );
    return response?.status === 200 ? response?.data : null;
  } catch (error) {
    const errorResponse = handleApiError(error as AxiosError);
    return errorResponse ? errorResponse : null;
  }
}

/**
 * Update course by ID
 */
export async function updateCourse(id: number, payload: Partial<ICourse>) {
  try {
    const response = await adminAxios.put(ADMIN_API_ENDPOINTS.COURSE.UPDATE(id), payload);
    return response?.status === 200 ? response?.data : null;
  } catch (error) {
    const errorResponse = handleApiError(error as AxiosError);
    return errorResponse ? errorResponse : null;
  }
}

/**
 * Delete course by ID
 */
export async function deleteCourse(id: number) {
  try {
    const response = await adminAxios.delete(ADMIN_API_ENDPOINTS.COURSE.DELETE(id));
    return response?.status === 200 ? response?.data : null;
  } catch (error) {
    const errorResponse = handleApiError(error as AxiosError);
    return errorResponse ? errorResponse : null;
  }
}

/**
 * Get course settings (variant and status)
 */
export async function getCourse() {
  try {
    const response = await getUniversitySettingsBySection(UNIVERSITY_SETTINGS_SECTION_TYPES.COURSES);
    if (response?.data) {
      return {
        data: {
          course_variant: response.data.variant,
          course_status: response.data.is_active,
          university_settings_id: response.data.id,
        }
      };
    } else if (response) {
      return {
        data: {
          course_variant: response.variant,
          course_status: response.is_active,
          university_settings_id: response.id,
        }
      };
    }
    return response;
  } catch (error) {
    const errorResponse = handleApiError(error as AxiosError);
    return errorResponse ? errorResponse : null;
  }
}
